/**
 * 剧本监测 · 发单情况
 * 选择业务单元查看今日剧本进度；不改动原发单监控界面。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  App,
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { FileTextOutlined, ReloadOutlined, StopOutlined } from "../components/icons";
import { buildFeishuMediaProxyUrl } from "../../shared/feishuCellValue";
import { useTenantContext } from "../tenant/TenantContext";
import { trpc } from "../trpc";
import {
  DISPATCH_SCRIPT_CONTENT_TYPE_LABELS,
  DISPATCH_SCRIPT_ROLE_KIND_LABELS,
  DISPATCH_SCRIPT_SLOT_LABELS,
  isDispatchScriptReferenceImageRow,
  type DispatchScriptContentType,
  type DispatchScriptSlotKind,
} from "../../shared/dispatchScriptSheet";
import {
  DISPATCH_SCRIPT_MATCH_STATUSES,
  DISPATCH_SCRIPT_MATCH_STATUS_LABELS,
  formatDispatchScriptTimeDeviationMinutes,
  formatShanghaiMinutesOfDay,
  getShanghaiDateKey,
  getShanghaiMinutesOfDay,
  type DispatchScriptMatchStatus,
} from "../../shared/dispatchScriptTime";
import { DISPATCH_SCRIPT_MATCH_TOLERANCE_MINUTES } from "../../shared/dispatchScriptSnapshot";

const { Text, Title, Paragraph } = Typography;

type AnyRecord = Record<string, any>;
type ImageFingerprintStatus =
  | "none"
  | "pending"
  | "downloading"
  | "computing"
  | "ready"
  | "failed";

const UNIT_STORAGE_KEY = "dispatchScriptProgressBusinessUnitId";

/** 默认不展示「未监测」 */
const DEFAULT_STATUS_FILTER: DispatchScriptMatchStatus[] = DISPATCH_SCRIPT_MATCH_STATUSES.filter(
  (status) => status !== "skipped"
);

const STATUS_FILTER_OPTIONS = DISPATCH_SCRIPT_MATCH_STATUSES.map((status) => ({
  value: status,
  label: DISPATCH_SCRIPT_MATCH_STATUS_LABELS[status],
}));

const ANOMALY_LABELS: Record<string, string> = {
  added: "新增内容",
  pending: "待人工判断",
};

const ANOMALY_DETAIL_LABELS: Record<string, string> = {
  added: "新增内容",
  pending: "待人工判断",
};

const ANOMALY_COLORS: Record<string, string> = {
  added: "processing",
  pending: "default",
};

function archiveMediaUrl(record: AnyRecord) {
  if (!record.sourceId || !record.mediaMd5) return "";
  if (!["image", "emotion", "sticker"].includes(String(record.msgtype).toLowerCase())) return "";
  return `/api/archive-media/${encodeURIComponent(record.sourceId)}/${encodeURIComponent(record.mediaMd5)}`;
}

function resolveImageFingerprintStatus(row: AnyRecord): ImageFingerprintStatus {
  if (row.imageDHash) return "ready";
  if ((row.materialId || row.attachedOssUrl) && row.imageFingerprintStatus === "failed") {
    return "failed";
  }
  if (row.materialId || row.attachedOssUrl) {
    return (row.imageFingerprintStatus as ImageFingerprintStatus | undefined) || "pending";
  }
  if (!row.imageFileToken) return "none";
  return (row.imageFingerprintStatus as ImageFingerprintStatus | undefined) || "pending";
}

function resolvePlannedMediaUrl(row: AnyRecord): string {
  const attachedUrl = String(row.attachedOssUrl || "").trim();
  if (attachedUrl) return attachedUrl;
  if (row.materialId) {
    const ossUrl = String(row.materialOssUrl || "").trim();
    if (ossUrl) return ossUrl;
    return `/api/script-material-media/${encodeURIComponent(String(row.materialId))}`;
  }
  if (row.imageFileToken && resolveImageFingerprintStatus(row) === "ready") {
    return buildFeishuMediaProxyUrl(row.imageFileToken);
  }
  return "";
}

function canBindMaterialToRow(row: AnyRecord): boolean {
  if (row.actualOnly || isDispatchScriptReferenceImageRow(row)) return false;
  const types = (row.contentTypes as string[] | undefined) || [];
  return types.includes("sticker") || types.includes("image");
}

function canUploadRowImage(row: AnyRecord): boolean {
  return !row.actualOnly && isDispatchScriptReferenceImageRow(row);
}

type MaterialPickerTypeFilter = "all" | "sticker" | "image";

function formatImageConfidence(value: unknown) {
  return typeof value === "number" && Number.isFinite(value)
    ? `${(value * 100).toFixed(1)}%`
    : "—";
}

function formatActualTimeRange(first: unknown, last: unknown) {
  const format = (value: unknown) => {
    const date = new Date(String(value || ""));
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleTimeString("zh-CN", {
      timeZone: "Asia/Shanghai",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };
  const start = format(first);
  const end = format(last);
  if (!start) return "";
  return !end || end === start ? start : `${start} - ${end}`;
}

function resolveDiffRowClass(row: AnyRecord, scriptDate: unknown) {
  if (row.actualOnly) return "dispatch-script-added-row";
  if (row.matchStatus === "skipped" || (row.actualItems || []).length > 0) return "";
  const presetMinutes = Number(row.time?.totalMinutes);
  if (!Number.isFinite(presetMinutes)) return "";
  const today = getShanghaiDateKey();
  const date = String(scriptDate || "");
  if (date > today) return "";
  if (
    date < today ||
    getShanghaiMinutesOfDay(new Date()) > presetMinutes + DISPATCH_SCRIPT_MATCH_TOLERANCE_MINUTES
  ) {
    return "dispatch-script-missing-row";
  }
  return "";
}

const ACTUAL_MEDIA_DISPLAY_MERGE_GAP_MS = 15 * 60 * 1000;

function groupActualContentItems(items: AnyRecord[]) {
  const clusters: AnyRecord[] = [];
  const latestByKey = new Map<string, AnyRecord>();
  const timeOf = (value: unknown) => {
    const timestamp = new Date(String(value || "")).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  };

  for (const item of [...items].sort((left, right) => timeOf(left.firstMessageAt) - timeOf(right.firstMessageAt))) {
    const mediaMd5 = String(item.mediaMd5 || "").toLowerCase();
    if (!mediaMd5) {
      clusters.push({ ...item, batchIds: item.batchId ? [item.batchId] : [] });
      continue;
    }
    const key = [
      item.status,
      item.detailKind,
      item.requirementIndex ?? "",
      item.msgtype,
      mediaMd5,
    ].join(":");
    const previous = latestByKey.get(key);
    const firstAt = timeOf(item.firstMessageAt);
    const previousLastAt = previous ? timeOf(previous.lastMessageAt) : 0;
    if (!previous || !firstAt || !previousLastAt || firstAt - previousLastAt > ACTUAL_MEDIA_DISPLAY_MERGE_GAP_MS) {
      const created = { ...item, batchIds: item.batchId ? [item.batchId] : [] };
      clusters.push(created);
      latestByKey.set(key, created);
      continue;
    }
    previous.messageCount = Number(previous.messageCount || 0) + Number(item.messageCount || 0);
    previous.roomCount = Number(previous.roomCount || 0) + Number(item.roomCount || 0);
    previous.firstMessageAt =
      firstAt < timeOf(previous.firstMessageAt) ? item.firstMessageAt : previous.firstMessageAt;
    previous.lastMessageAt =
      timeOf(item.lastMessageAt) > previousLastAt ? item.lastMessageAt : previous.lastMessageAt;
    if (item.batchId && !previous.batchIds.includes(item.batchId)) previous.batchIds.push(item.batchId);
  }
  return clusters;
}

type ScriptContentItem = {
  id: string;
  text?: string;
  mediaUrl?: string;
  msgtype?: string;
  mediaMd5?: string;
  sourceId?: string;
  imageDHash?: string;
  status?: string;
  detailKind?: string;
  statusLabel?: string;
  statusColor?: string;
  messageCount?: number;
  roomCount?: number;
  timeRange?: string;
  batchIds?: string[];
  actions?: Array<{
    key: string;
    label: string;
    danger?: boolean;
    disabled?: boolean;
    onClick: () => void;
  }>;
};

function ScriptContentCell(props: {
  items: ScriptContentItem[];
  emptyLabel: string;
  onOpenBatch?: (batchId: string) => void;
}) {
  if (!props.items.length) {
    return (
      <div className="dispatch-script-content-cell dispatch-script-content-cell--empty">
        <Text type="secondary">{props.emptyLabel}</Text>
      </div>
    );
  }
  return (
    <div className="dispatch-script-content-cell">
      {props.items.map((item) => {
        const isImageContent = ["image", "emotion", "sticker"].includes(
          String(item.msgtype).toLowerCase()
        );
        const mediaMd5 = isImageContent && item.mediaMd5 ? String(item.mediaMd5) : "";
        const imageDHash = isImageContent && item.imageDHash ? String(item.imageDHash) : "";
        return (
          <div key={item.id} className="dispatch-script-content-item">
            {item.mediaUrl ? (
              <div className="dispatch-script-content-media-slot">
                <img
                  src={item.mediaUrl}
                  alt={item.text || item.msgtype || "图片"}
                  loading="lazy"
                  className="dispatch-script-content-image"
                />
              </div>
            ) : null}
            {item.text ? <Text className="dispatch-script-content-text">{item.text}</Text> : null}
            {mediaMd5 || imageDHash ? (
              <Text type="secondary" className="dispatch-script-content-fingerprint">
                {mediaMd5 ? (
                  <>
                    MD5{" "}
                    <Text code copyable={{ text: mediaMd5 }}>
                      {mediaMd5}
                    </Text>
                  </>
                ) : null}
                {imageDHash ? (
                  <>
                    {mediaMd5 ? " · " : null}
                    dHash{" "}
                    <Text code copyable={{ text: imageDHash }}>
                      {imageDHash}
                    </Text>
                  </>
                ) : null}
              </Text>
            ) : null}
            {item.statusLabel ? (
              <Space size={4} wrap className="dispatch-script-content-meta">
                <Tag color={item.statusColor}>{item.statusLabel}</Tag>
                {item.msgtype ? <Tag>{item.msgtype}</Tag> : null}
                {item.messageCount != null ? (
                  <Text type="secondary">{item.messageCount || 0} 条 / {item.roomCount || 0} 群</Text>
                ) : null}
                {item.timeRange ? <Text type="secondary">{item.timeRange}</Text> : null}
                {(item.batchIds || []).length > 1 ? <Tag>{item.batchIds!.length} 批</Tag> : null}
                {(item.batchIds || []).map((batchId: string, index: number) => (
                  <Button
                    key={batchId}
                    type="text"
                    size="small"
                    icon={<FileTextOutlined />}
                    title={
                      (item.batchIds || []).length > 1
                        ? `查看第 ${index + 1} 个批次消息`
                        : "查看批次消息"
                    }
                    onClick={() => props.onOpenBatch?.(batchId)}
                  />
                ))}
              </Space>
            ) : null}
            {item.actions?.length ? (
              <Space size={4} wrap>
                {item.actions.map((action) => (
                  <Button
                    key={action.key}
                    type="link"
                    size="small"
                    danger={action.danger}
                    disabled={action.disabled}
                    onClick={action.onClick}
                  >
                    {action.label}
                  </Button>
                ))}
              </Space>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function DispatchScriptProgressPage() {
  const { message } = App.useApp();
  const { isAdmin } = useTenantContext();
  const [businessUnits, setBusinessUnits] = useState<AnyRecord[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState<string | undefined>(() => {
    return localStorage.getItem(UNIT_STORAGE_KEY) || undefined;
  });
  const [scriptSnapshot, setScriptSnapshot] = useState<AnyRecord | null>(null);
  const [statusFilter, setStatusFilter] = useState<DispatchScriptMatchStatus[]>(DEFAULT_STATUS_FILTER);
  const [loading, setLoading] = useState(false);
  const [scriptLoading, setScriptLoading] = useState(false);
  const [capturingScript, setCapturingScript] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const [batchReview, setBatchReview] = useState<{
    batchId: string;
    messages: AnyRecord[];
    loading: boolean;
    page: number;
    total: number;
  } | null>(null);
  const [adjustReason, setAdjustReason] = useState("");
  const [adjustingMessageId, setAdjustingMessageId] = useState("");
  const [materialPicker, setMaterialPicker] = useState<{
    rowKey: string;
  } | null>(null);
  const [materialTypeFilter, setMaterialTypeFilter] =
    useState<MaterialPickerTypeFilter>("all");
  const [materialKeyword, setMaterialKeyword] = useState("");
  const [materialList, setMaterialList] = useState<AnyRecord[]>([]);
  const [materialLoading, setMaterialLoading] = useState(false);
  const [materialBinding, setMaterialBinding] = useState(false);
  const [savingMaterialKey, setSavingMaterialKey] = useState("");
  const [uploadingRowKey, setUploadingRowKey] = useState("");
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const hasAutoLocatedRef = useRef<string | null>(null);
  const rowImageInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadRowRef = useRef<AnyRecord | null>(null);

  const loadUnits = useCallback(async () => {
    setLoading(true);
    try {
      const units = await trpc.fadan.businessUnits.list.query();
      setBusinessUnits(units);
      setSelectedUnitId((prev) => {
        const next =
          (prev && units.some((unit) => unit.id === prev) ? prev : undefined) || units[0]?.id;
        if (next) localStorage.setItem(UNIT_STORAGE_KEY, next);
        return next;
      });
    } catch (error: any) {
      message.error(error?.message || "加载业务单元失败");
    } finally {
      setLoading(false);
    }
  }, [message]);

  const loadScriptSnapshot = useCallback(
    async (
      businessUnitId: string,
      options?: {
        silent?: boolean;
        page?: number;
        statuses?: DispatchScriptMatchStatus[];
        locateCurrent?: boolean;
      }
    ) => {
      if (!options?.silent) setScriptLoading(true);
      try {
        const snapshot = await trpc.dispatchScript.getTodaySnapshot.query({
          businessUnitId,
          page: options?.page || 1,
          pageSize: 30,
          statuses: options?.statuses || DEFAULT_STATUS_FILTER,
          locateCurrent: options?.locateCurrent || false,
        });
        setScriptSnapshot(snapshot);
        setTablePage(snapshot?.pagination?.page || options?.page || 1);
      } catch (error: any) {
        setScriptSnapshot(null);
        message.error(error?.message || "加载今日剧本失败");
      } finally {
        if (!options?.silent) setScriptLoading(false);
      }
    },
    [message]
  );

  const handleCaptureScript = useCallback(async () => {
    if (!selectedUnitId) return;
    setCapturingScript(true);
    try {
      const result = await trpc.dispatchScript.captureToday.mutate({
        businessUnitId: selectedUnitId,
      });
      const snapshot = result.mode === "single" ? result.snapshot : null;
      if (snapshot?.lastCaptureStatus && snapshot.lastCaptureStatus !== "ok") {
        message.warning(
          `刷新失败，已保留原监测结果：${snapshot.lastCaptureError || snapshot.lastCaptureStatus}`
        );
        await loadScriptSnapshot(selectedUnitId, {
          page: 1,
          statuses: statusFilter,
          locateCurrent: true,
        });
        return;
      }
      const monitored =
        (snapshot?.stats?.pending ?? 0) +
        (snapshot?.stats?.exact ?? 0) +
        (snapshot?.stats?.short ?? 0) +
        (snapshot?.stats?.excess ?? 0);
      const skipped = snapshot?.stats?.skipped ?? 0;
      const fromLabel =
        typeof snapshot?.monitorFromMinutes === "number"
          ? formatShanghaiMinutesOfDay(snapshot.monitorFromMinutes)
          : "";
      message.success(
        fromLabel
          ? `已生成快照并开始检测（从 ${fromLabel} 起，监测 ${monitored} 行，跳过 ${skipped} 行）`
          : "已生成快照并开始检测"
      );
      await loadScriptSnapshot(selectedUnitId, {
        page: 1,
        statuses: statusFilter,
        locateCurrent: true,
      });
    } catch (error: any) {
      message.error(error?.message || "生成剧本失败");
    } finally {
      setCapturingScript(false);
    }
  }, [selectedUnitId, message, loadScriptSnapshot, statusFilter]);

  useEffect(() => {
    void loadUnits();
  }, [loadUnits]);

  useEffect(() => {
    if (!selectedUnitId) {
      setScriptSnapshot(null);
      return;
    }
    localStorage.setItem(UNIT_STORAGE_KEY, selectedUnitId);
    void loadScriptSnapshot(selectedUnitId, {
      page: 1,
      statuses: statusFilter,
      locateCurrent: true,
    });
  }, [selectedUnitId, statusFilter, loadScriptSnapshot]);

  const selectedUnit = businessUnits.find((unit) => unit.id === selectedUnitId);

  const visibleRows = (scriptSnapshot?.rows || []) as AnyRecord[];

  const loadBatchMessages = useCallback(
    async (batchId: string, page = 1) => {
      if (!selectedUnitId) return;
      setBatchReview((current) => ({
        batchId,
        messages: current?.batchId === batchId ? current.messages : [],
        loading: true,
        page,
        total: current?.batchId === batchId ? current.total : 0,
      }));
      try {
        const result = await trpc.fadan.batchMessages.query({
          batchId,
          businessUnitId: selectedUnitId,
          page,
          limit: 50,
        });
        setBatchReview({
          batchId,
          messages: result.items,
          loading: false,
          page: result.page,
          total: result.total,
        });
      } catch (error: any) {
        setBatchReview(null);
        message.error(error?.message || "加载批次消息失败");
      }
    },
    [message, selectedUnitId]
  );

  const adjustMessage = useCallback(
    async (messageId: string, action: "exclude" | "restore") => {
      if (action === "exclude" && !adjustReason.trim()) {
        message.warning("请输入排除原因");
        return;
      }
      setAdjustingMessageId(messageId);
      try {
        await trpc.fadan.adjustMessage.mutate({
          messageId,
          action,
          reason: action === "exclude" ? adjustReason.trim() : undefined,
        });
        message.success(action === "exclude" ? "消息已排除" : "消息已恢复");
        if (batchReview?.batchId) await loadBatchMessages(batchReview.batchId, batchReview.page);
        if (selectedUnitId) {
          await loadScriptSnapshot(selectedUnitId, {
            silent: true,
            page: tablePage,
            statuses: statusFilter,
          });
        }
      } catch (error: any) {
        message.error(error?.message || "调整消息失败");
      } finally {
        setAdjustingMessageId("");
      }
    },
    [adjustReason, batchReview?.batchId, batchReview?.page, loadBatchMessages, loadScriptSnapshot, message, selectedUnitId, statusFilter, tablePage]
  );

  const loadMaterialPickerList = useCallback(
    async (typeFilter: MaterialPickerTypeFilter, keyword?: string) => {
      setMaterialLoading(true);
      try {
        const result = await trpc.dispatchScript.materialList.query({
          type: typeFilter === "all" ? undefined : typeFilter,
          keyword: keyword?.trim() || undefined,
          page: 1,
          pageSize: 48,
        });
        // 视频不可绑定到预定行（无 dHash），「全部」时前端过滤掉
        const items =
          typeFilter === "all"
            ? result.items.filter((item) => item.type !== "video")
            : result.items;
        setMaterialList(items);
      } catch (error: any) {
        message.error(error?.message || "加载素材失败");
        setMaterialList([]);
      } finally {
        setMaterialLoading(false);
      }
    },
    [message]
  );

  const openMaterialPicker = useCallback(
    async (row: AnyRecord) => {
      setMaterialPicker({ rowKey: String(row.rowKey) });
      setMaterialTypeFilter("all");
      setMaterialKeyword("");
      await loadMaterialPickerList("all", "");
    },
    [loadMaterialPickerList]
  );

  const handleMaterialTypeFilterChange = useCallback(
    (value: MaterialPickerTypeFilter) => {
      setMaterialTypeFilter(value);
      void loadMaterialPickerList(value, materialKeyword);
    },
    [loadMaterialPickerList, materialKeyword]
  );

  const bindMaterial = useCallback(
    async (materialId: string | null) => {
      if (!selectedUnitId || !materialPicker || !scriptSnapshot?.scriptDate) return;
      setMaterialBinding(true);
      try {
        await trpc.dispatchScript.bindRowMaterial.mutate({
          businessUnitId: selectedUnitId,
          scriptDate: String(scriptSnapshot.scriptDate),
          rowKey: materialPicker.rowKey,
          materialId,
        });
        message.success(materialId ? "已绑定素材" : "已解除绑定");
        setMaterialPicker(null);
        await loadScriptSnapshot(selectedUnitId, {
          silent: true,
          page: tablePage,
          statuses: statusFilter,
        });
      } catch (error: any) {
        message.error(error?.message || "绑定失败");
      } finally {
        setMaterialBinding(false);
      }
    },
    [
      loadScriptSnapshot,
      materialPicker,
      message,
      scriptSnapshot?.scriptDate,
      selectedUnitId,
      statusFilter,
      tablePage,
    ]
  );

  const saveActualAsMaterial = useCallback(
    async (item: AnyRecord, row?: AnyRecord) => {
      const sourceId = String(item.sourceId || "").trim();
      const mediaMd5 = String(item.mediaMd5 || "").trim();
      const msgtype = String(item.msgtype || "").toLowerCase();
      if (!sourceId || !mediaMd5) {
        message.warning(
          row && canUploadRowImage(row)
            ? "缺少媒体信息，无法用作参考图"
            : "缺少媒体信息，无法保存为素材"
        );
        return;
      }
      const type =
        msgtype === "image" ? ("image" as const) : ("sticker" as const);
      const key = `${sourceId}:${mediaMd5}`;
      setSavingMaterialKey(key);
      try {
        if (row && canUploadRowImage(row) && selectedUnitId && scriptSnapshot?.scriptDate) {
          await trpc.dispatchScript.attachRowImageFromArchive.mutate({
            businessUnitId: selectedUnitId,
            scriptDate: String(scriptSnapshot.scriptDate),
            rowKey: String(row.rowKey),
            sourceId,
            mediaMd5,
          });
          message.success("已用作本行参考图");
          await loadScriptSnapshot(selectedUnitId, {
            silent: true,
            page: tablePage,
            statuses: statusFilter,
          });
          return;
        }
        const result = await trpc.dispatchScript.materialCreateFromArchive.mutate({
          sourceId,
          mediaMd5,
          type,
        });
        const materialId = String(result.material.id);
        message.success(result.created ? "已保存为素材" : "素材已存在，可直接关联");
        if (
          row &&
          canBindMaterialToRow(row) &&
          selectedUnitId &&
          scriptSnapshot?.scriptDate
        ) {
          await trpc.dispatchScript.bindRowMaterial.mutate({
            businessUnitId: selectedUnitId,
            scriptDate: String(scriptSnapshot.scriptDate),
            rowKey: String(row.rowKey),
            materialId,
          });
          message.success("已绑定到当前预定行");
          await loadScriptSnapshot(selectedUnitId, {
            silent: true,
            page: tablePage,
            statuses: statusFilter,
          });
        }
      } catch (error: any) {
        message.error(error?.message || "保存失败");
      } finally {
        setSavingMaterialKey("");
      }
    },
    [
      loadScriptSnapshot,
      message,
      scriptSnapshot?.scriptDate,
      selectedUnitId,
      statusFilter,
      tablePage,
    ]
  );

  const uploadRowImageFile = useCallback(
    async (row: AnyRecord, file: File) => {
      if (!selectedUnitId || !scriptSnapshot?.scriptDate) return;
      const rowKey = String(row.rowKey);
      setUploadingRowKey(rowKey);
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("businessUnitId", selectedUnitId);
        formData.append("scriptDate", String(scriptSnapshot.scriptDate));
        formData.append("rowKey", rowKey);
        const response = await fetch("/api/upload/script-row-image", {
          method: "POST",
          body: formData,
          credentials: "include",
        });
        const payload = (await response.json().catch(() => ({}))) as {
          error?: string;
          imageFingerprintStatus?: string;
          imageFingerprintError?: string;
        };
        if (!response.ok) {
          throw new Error(payload.error || `上传失败 (HTTP ${response.status})`);
        }
        message.success(
          payload.imageFingerprintStatus === "failed"
            ? `已上传，但 dHash 计算失败：${payload.imageFingerprintError || "未知错误"}`
            : "参考图已上传"
        );
        await loadScriptSnapshot(selectedUnitId, {
          silent: true,
          page: tablePage,
          statuses: statusFilter,
        });
      } catch (error: any) {
        message.error(error?.message || "上传失败");
      } finally {
        setUploadingRowKey("");
      }
    },
    [loadScriptSnapshot, message, scriptSnapshot?.scriptDate, selectedUnitId, statusFilter, tablePage]
  );

  const pickRowImage = useCallback((row: AnyRecord) => {
    pendingUploadRowRef.current = row;
    rowImageInputRef.current?.click();
  }, []);

  const clearRowAttachedImage = useCallback(
    async (row: AnyRecord) => {
      if (!selectedUnitId || !scriptSnapshot?.scriptDate) return;
      try {
        await trpc.dispatchScript.clearRowAttachedImage.mutate({
          businessUnitId: selectedUnitId,
          scriptDate: String(scriptSnapshot.scriptDate),
          rowKey: String(row.rowKey),
        });
        message.success("已清除参考图");
        await loadScriptSnapshot(selectedUnitId, {
          silent: true,
          page: tablePage,
          statuses: statusFilter,
        });
      } catch (error: any) {
        message.error(error?.message || "清除失败");
      }
    },
    [loadScriptSnapshot, message, scriptSnapshot?.scriptDate, selectedUnitId, statusFilter, tablePage]
  );

  const currentRowKey = String(scriptSnapshot?.pagination?.currentRowKey || "");

  useEffect(() => {
    if (!scriptSnapshot?.id || !currentRowKey || visibleRows.length === 0) return;
    const locateKey = `${scriptSnapshot.id}:${statusFilter.join(",")}:${tablePage}`;
    if (hasAutoLocatedRef.current === locateKey) return;
    hasAutoLocatedRef.current = locateKey;
    window.requestAnimationFrame(() => {
      tableContainerRef.current
        ?.querySelector<HTMLElement>(`[data-row-key="${currentRowKey}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [currentRowKey, visibleRows, scriptSnapshot?.id, statusFilter, tablePage]);

  return (
    <Space orientation="vertical" size={16} style={{ width: "100%" }}>
      <div className="page-title-row">
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            <FileTextOutlined style={{ marginRight: 8 }} />
            发单情况
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            按业务单元查看今日剧本匹配进度（正好 / 少了 / 多了 / 未监测）。
          </Paragraph>
        </div>
        <Space wrap>
          <Select
            style={{ minWidth: 220 }}
            placeholder="选择业务单元"
            loading={loading}
            value={selectedUnitId}
            options={businessUnits.map((unit) => ({
              value: unit.id,
              label: unit.name,
            }))}
            onChange={(value) => setSelectedUnitId(value)}
            showSearch={{ optionFilterProp: "label" }}
          />
          <Button
            icon={<ReloadOutlined />}
            loading={scriptLoading}
            disabled={!selectedUnitId}
            onClick={() =>
              selectedUnitId &&
              loadScriptSnapshot(selectedUnitId, {
                page: tablePage,
                statuses: statusFilter,
              })
            }
          >
            刷新
          </Button>
          {isAdmin ? (
            <Button
              type="primary"
              loading={capturingScript}
              disabled={!selectedUnitId}
              onClick={handleCaptureScript}
            >
              立即生成并开始检测
            </Button>
          ) : null}
        </Space>
      </div>

      <Card variant="borderless" className="shadow-sm">
        <Spin spinning={scriptLoading || loading}>
          {!selectedUnitId ? (
            <Empty description="暂无可用业务单元" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : !selectedUnit?.scriptSpreadsheetToken ? (
            <Empty
              description="该业务单元尚未绑定剧本表格，请先到「剧本绑定」完成关联"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : !scriptSnapshot ? (
            <Empty
              description="暂无今日剧本快照（可点「立即生成并开始检测」，或等每天 5:00 自动生成）"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : scriptSnapshot.captureStatus !== "ok" ? (
            <Alert
              type="warning"
              showIcon
              title={`快照状态：${scriptSnapshot.captureStatus}`}
              description={scriptSnapshot.captureError || "未能生成有效剧本行"}
            />
          ) : (
            <Space orientation="vertical" size={12} style={{ width: "100%" }}>
              <Space wrap>
                <Tag>日期 {scriptSnapshot.scriptDate}</Tag>
                <Tag>工作表 {scriptSnapshot.sheetTitle || "—"}</Tag>
                <Tag>配置发单群 {scriptSnapshot.configuredRoomCount}</Tag>
                {typeof scriptSnapshot.monitorFromMinutes === "number" ? (
                  <Tag color="blue">
                    监测起点 {formatShanghaiMinutesOfDay(scriptSnapshot.monitorFromMinutes)}
                  </Tag>
                ) : null}
                <Tag color="default">待匹配 {scriptSnapshot.stats?.pending ?? 0}</Tag>
                <Tag color="success">正好 {scriptSnapshot.stats?.exact ?? 0}</Tag>
                <Tag color="warning">少了 {scriptSnapshot.stats?.short ?? 0}</Tag>
                <Tag color="error">多了 {scriptSnapshot.stats?.excess ?? 0}</Tag>
                <Tag>未监测 {scriptSnapshot.stats?.skipped ?? 0}</Tag>
                <Tag color="processing">新增内容 {scriptSnapshot.actualStats?.added ?? 0}</Tag>
                <Tag>待判断 {scriptSnapshot.actualStats?.pending ?? 0}</Tag>
              </Space>
              {(scriptSnapshot.warnings || []).length > 0 ? (
                <Alert
                  type="info"
                  showIcon
                  title={(scriptSnapshot.warnings as string[]).join("；")}
                />
              ) : null}
              <Space wrap>
                <Text type="secondary">状态筛选</Text>
                <Select
                  mode="multiple"
                  allowClear
                  style={{ minWidth: 280 }}
                  placeholder="选择要显示的状态"
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={(value) => {
                    setStatusFilter(value as DispatchScriptMatchStatus[]);
                    setTablePage(1);
                    hasAutoLocatedRef.current = null;
                  }}
                  maxTagCount="responsive"
                />
                <Text type="secondary">
                  本页 {visibleRows.length} 行，共 {scriptSnapshot.pagination?.total || 0} 行
                </Text>
              </Space>
              <div ref={tableContainerRef}>
                <Table
                  size="small"
                  rowKey={(row: AnyRecord) => String(row.rowKey)}
                  rowClassName={(row: AnyRecord) =>
                    [
                      resolveDiffRowClass(row, scriptSnapshot.scriptDate),
                      String(row.rowKey) === currentRowKey ? "dispatch-script-current-row" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")
                  }
                  dataSource={visibleRows}
                  pagination={{
                    current: tablePage,
                    pageSize: 30,
                    total: scriptSnapshot.pagination?.total || 0,
                    showSizeChanger: false,
                    onChange: (page) => {
                      setTablePage(page);
                      if (selectedUnitId) {
                        void loadScriptSnapshot(selectedUnitId, {
                          page,
                          statuses: statusFilter,
                        });
                      }
                    },
                  }}
                  sticky
                  scroll={{ x: 1320, y: "calc(100vh - 360px)" }}
                columns={[
                  {
                    title: "时间",
                    width: 100,
                    render: (_: unknown, row: AnyRecord) =>
                      row.time?.display ||
                      `${String(Math.floor(row.windowStartMinutes / 60)).padStart(2, "0")}:${String(
                        row.windowStartMinutes % 60
                      ).padStart(2, "0")}`,
                  },
                  {
                    title: "角色",
                    width: 120,
                    render: (_: unknown, row: AnyRecord) => (
                      <Space size={4} wrap>
                        <Tag>
                          {DISPATCH_SCRIPT_ROLE_KIND_LABELS[
                            row.roleKind as keyof typeof DISPATCH_SCRIPT_ROLE_KIND_LABELS
                          ] || row.roleKind}
                        </Tag>
                        <Text>{row.role}</Text>
                      </Space>
                    ),
                  },
                  {
                    title: "类型",
                    width: 160,
                    render: (_: unknown, row: AnyRecord) => (
                      <Space size={[4, 4]} wrap>
                        {(row.contentTypes as DispatchScriptContentType[] | undefined)?.map((type) => (
                          <Tag key={type}>{DISPATCH_SCRIPT_CONTENT_TYPE_LABELS[type]}</Tag>
                        ))}
                        {(row.slots as DispatchScriptSlotKind[] | undefined)?.map((slot) => (
                          <Tag key={slot} color="magenta">
                            {DISPATCH_SCRIPT_SLOT_LABELS[slot]}
                          </Tag>
                        ))}
                      </Space>
                    ),
                  },
                  {
                    title: "预定剧本",
                    width: 300,
                    onCell: () => ({ className: "dispatch-script-content-column-cell" }),
                    render: (_: unknown, row: AnyRecord) => {
                      const imageStatus = resolveImageFingerprintStatus(row);
                      const statusLabel =
                        imageStatus === "downloading"
                          ? "正在从飞书下载"
                          : imageStatus === "computing"
                            ? "正在计算 dHash"
                            : imageStatus === "pending"
                              ? "等待指纹就绪"
                              : imageStatus === "failed"
                            ? "图片指纹计算失败"
                            : "";
                      const mediaUrl = resolvePlannedMediaUrl(row);
                      const hasPlannedContent = Boolean(
                        mediaUrl ||
                          row.imageFileToken ||
                          row.text ||
                          row.materialId ||
                          row.attachedOssUrl
                      );
                      const bindable = canBindMaterialToRow(row);
                      const uploadable = canUploadRowImage(row);
                      const uploading = uploadingRowKey === String(row.rowKey);
                      const hasAttachedReference = Boolean(
                        String(row.attachedOssUrl || "").trim() || row.materialId
                      );
                      const actions: ScriptContentItem["actions"] = [];
                      if (uploadable) {
                        actions.push({
                          key: "upload",
                          label: uploading
                            ? "上传中…"
                            : hasAttachedReference
                              ? "更换图片"
                              : "上传图片",
                          disabled: uploading,
                          onClick: () => pickRowImage(row),
                        });
                        if (hasAttachedReference) {
                          actions.push({
                            key: "clear-attached",
                            label: "清除",
                            danger: true,
                            disabled: uploading,
                            onClick: () => void clearRowAttachedImage(row),
                          });
                        }
                      } else if (bindable) {
                        actions.push({
                          key: "bind",
                          label: row.materialId ? "更换素材" : "选择素材",
                          onClick: () => void openMaterialPicker(row),
                        });
                        if (row.materialId) {
                          actions.push({
                            key: "unbind",
                            label: "解绑",
                            danger: true,
                            onClick: () => {
                              void (async () => {
                                if (!selectedUnitId || !scriptSnapshot?.scriptDate) return;
                                try {
                                  await trpc.dispatchScript.bindRowMaterial.mutate({
                                    businessUnitId: selectedUnitId,
                                    scriptDate: String(scriptSnapshot.scriptDate),
                                    rowKey: String(row.rowKey),
                                    materialId: null,
                                  });
                                  message.success("已解除绑定");
                                  await loadScriptSnapshot(selectedUnitId, {
                                    silent: true,
                                    page: tablePage,
                                    statuses: statusFilter,
                                  });
                                } catch (error: any) {
                                  message.error(error?.message || "解绑失败");
                                }
                              })();
                            },
                          });
                        }
                      }
                      return (
                        <ScriptContentCell
                          emptyLabel={row.actualOnly ? "无预定内容" : "—"}
                          items={
                            hasPlannedContent || actions.length
                              ? [
                                  {
                                    id: `planned:${row.rowKey}`,
                                    text: row.text,
                                    mediaUrl: mediaUrl || undefined,
                                    msgtype:
                                      row.attachedOssUrl ||
                                      row.materialId ||
                                      row.imageFileToken
                                        ? "image"
                                        : "text",
                                    mediaMd5: row.imageMd5,
                                    imageDHash: row.imageDHash,
                                    statusLabel: row.attachedOssUrl
                                      ? imageStatus === "failed"
                                        ? "参考图指纹计算失败"
                                        : "已上传参考图"
                                      : row.materialId
                                        ? "素材已关联"
                                        : uploadable
                                          ? "待上传参考图"
                                          : row.imageFileToken && imageStatus !== "ready"
                                            ? statusLabel
                                            : undefined,
                                    statusColor:
                                      imageStatus === "failed" ? "error" : "processing",
                                    actions,
                                  },
                                ]
                              : []
                          }
                        />
                      );
                    },
                  },
                  {
                    title: "实际发单内容",
                    width: 360,
                    onCell: () => ({ className: "dispatch-script-content-column-cell" }),
                    render: (_: unknown, row: AnyRecord) => {
                      const actualItems = groupActualContentItems(
                        (row.actualItems || []) as AnyRecord[]
                      ).map((item): ScriptContentItem => {
                        const msgtype = String(item.msgtype || "").toLowerCase();
                        const canSave = ["image", "emotion", "sticker"].includes(msgtype);
                        const sourceId = String(item.sourceId || "");
                        const mediaMd5 = String(item.mediaMd5 || "");
                        const savingKey = `${sourceId}:${mediaMd5}`;
                        return {
                          id: String(item.id),
                          text: item.content,
                          mediaUrl: archiveMediaUrl(item),
                          msgtype: item.msgtype,
                          mediaMd5: item.mediaMd5,
                          sourceId: item.sourceId,
                          imageDHash: item.imageDHash,
                          statusLabel:
                            ANOMALY_DETAIL_LABELS[item.detailKind] ||
                            ANOMALY_LABELS[item.status] ||
                            item.status,
                          statusColor: ANOMALY_COLORS[item.status],
                          messageCount: item.messageCount,
                          roomCount: item.roomCount,
                          timeRange: formatActualTimeRange(
                            item.firstMessageAt,
                            item.lastMessageAt
                          ),
                          batchIds: item.batchIds,
                          actions: canSave
                            ? [
                                {
                                  key: "save-material",
                                  label:
                                    savingMaterialKey === savingKey
                                      ? "保存中…"
                                      : canUploadRowImage(row)
                                        ? "用作参考图"
                                        : "保存为素材",
                                  onClick: () => void saveActualAsMaterial(item, row),
                                },
                              ]
                            : undefined,
                        };
                      });
                      return (
                        <ScriptContentCell
                          items={actualItems}
                          emptyLabel="尚未发出"
                          onOpenBatch={loadBatchMessages}
                        />
                      );
                    },
                  },
                  {
                    title: "命中/配置",
                    width: 110,
                    render: (_: unknown, row: AnyRecord) =>
                      `${row.hitRoomCount || 0}/${scriptSnapshot.configuredRoomCount}`,
                  },
                  {
                    title: "消息数",
                    width: 80,
                    dataIndex: "messageCount",
                  },
                  {
                    title: "时间偏离",
                    width: 130,
                    render: (_: unknown, row: AnyRecord) => {
                      if (row.timeDeviationSampleCount == null || row.timeDeviationSampleCount <= 0) {
                        return <Text type="secondary">—</Text>;
                      }
                      const avg = formatDispatchScriptTimeDeviationMinutes(row.timeDeviationAvgMinutes);
                      const min = formatDispatchScriptTimeDeviationMinutes(row.timeDeviationMinMinutes);
                      const max = formatDispatchScriptTimeDeviationMinutes(row.timeDeviationMaxMinutes);
                      const actualTimeRange = formatActualTimeRange(
                        row.firstReceivedAt,
                        row.lastReceivedAt
                      );
                      const color =
                        typeof row.timeDeviationAvgMinutes === "number" &&
                        Math.abs(row.timeDeviationAvgMinutes) >= 5
                          ? "#b45309"
                          : undefined;
                      return (
                        <Tooltip title={`最早 ${min} / 最晚 ${max}（相对剧本预设）`}>
                          <Space orientation="vertical" size={0}>
                            <Text style={{ color }}>{avg}</Text>
                            {actualTimeRange ? (
                              <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                                {actualTimeRange}
                              </Text>
                            ) : null}
                          </Space>
                        </Tooltip>
                      );
                    },
                  },
                  {
                    title: "图片置信度",
                    width: 120,
                    render: (_: unknown, row: AnyRecord) => {
                      if (!row.imageMatchConfidenceSampleCount) {
                        return <Text type="secondary">—</Text>;
                      }
                      const avg = formatImageConfidence(row.imageMatchConfidenceAvg);
                      const min = formatImageConfidence(row.imageMatchConfidenceMin);
                      const max = formatImageConfidence(row.imageMatchConfidenceMax);
                      const color =
                        typeof row.imageMatchConfidenceAvg === "number" &&
                        row.imageMatchConfidenceAvg < 0.85
                          ? "#b45309"
                          : "#15803d";
                      return (
                        <Tooltip
                          title={`最低 ${min} / 最高 ${max}，共 ${row.imageMatchConfidenceSampleCount} 个群图片命中`}
                        >
                          <Text style={{ color }}>{avg}</Text>
                        </Tooltip>
                      );
                    },
                  },
                  {
                    title: "状态",
                    width: 130,
                    render: (_: unknown, row: AnyRecord) => {
                      const status = row.matchStatus as DispatchScriptMatchStatus;
                      const color =
                        status === "exact"
                          ? "success"
                          : status === "short"
                            ? "warning"
                            : status === "excess"
                              ? "error"
                              : status === "skipped"
                                ? "default"
                                : "default";
                      const actualStatuses = ((row.actualStatuses || []) as string[]).filter(
                        (item) => item !== "exact"
                      );
                      return (
                        <Space orientation="vertical" size={4}>
                          <Tag color={color}>{DISPATCH_SCRIPT_MATCH_STATUS_LABELS[status] || status}</Tag>
                          {actualStatuses.map((item) => (
                            <Tag key={item} color={ANOMALY_COLORS[item]}>
                              {ANOMALY_LABELS[item] || item}
                            </Tag>
                          ))}
                        </Space>
                      );
                    },
                  },
                  ]}
                />
              </div>
            </Space>
          )}
        </Spin>
      </Card>
      <Modal
        open={Boolean(batchReview)}
        title="批次消息审查"
        width="min(1100px, 94vw)"
        footer={null}
        onCancel={() => {
          setBatchReview(null);
          setAdjustReason("");
        }}
      >
        {isAdmin ? (
          <Input
            value={adjustReason}
            maxLength={500}
            placeholder="输入排除原因"
            style={{ marginBottom: 12 }}
            onChange={(event) => setAdjustReason(event.target.value)}
          />
        ) : null}
        <Table
          size="small"
          rowKey="id"
          loading={batchReview?.loading}
          dataSource={batchReview?.messages || []}
          pagination={{
            current: batchReview?.page || 1,
            pageSize: 50,
            total: batchReview?.total || 0,
            showSizeChanger: false,
            onChange: (page) => batchReview?.batchId && loadBatchMessages(batchReview.batchId, page),
          }}
          scroll={{ x: 900 }}
          columns={[
            { title: "时间", dataIndex: "messageTime", width: 180, render: (value) => new Date(value).toLocaleString("zh-CN") },
            { title: "群", dataIndex: "roomName", width: 180 },
            { title: "发送人", dataIndex: "senderName", width: 140 },
            { title: "类型", dataIndex: "msgtype", width: 90, render: (value) => <Tag>{value}</Tag> },
            {
              title: "内容",
              render: (_: unknown, record: AnyRecord) => (
                <Text delete={Boolean(record.excludedAt)}>{record.content || `[${record.msgtype}]`}</Text>
              ),
            },
            ...(isAdmin
              ? [
                  {
                    title: "操作",
                    width: 90,
                    render: (_: unknown, record: AnyRecord) =>
                      record.excludedAt ? (
                        <Button
                          type="text"
                          icon={<ReloadOutlined />}
                          loading={adjustingMessageId === record.id}
                          title="恢复消息"
                          onClick={() => adjustMessage(record.id, "restore")}
                        />
                      ) : (
                        <Popconfirm
                          title="确认排除这条消息？"
                          description="批次计数和实际发单内容将立即更新"
                          onConfirm={() => adjustMessage(record.id, "exclude")}
                        >
                          <Button
                            type="text"
                            danger
                            icon={<StopOutlined />}
                            loading={adjustingMessageId === record.id}
                            title="排除消息"
                          />
                        </Popconfirm>
                      ),
                  },
                ]
              : []),
          ]}
        />
      </Modal>
      <Modal
        open={Boolean(materialPicker)}
        title="选择素材"
        width="min(720px, 94vw)"
        footer={null}
        onCancel={() => setMaterialPicker(null)}
      >
        <Space orientation="vertical" size={12} style={{ width: "100%" }}>
          <Segmented
            value={materialTypeFilter}
            onChange={(value) =>
              handleMaterialTypeFilterChange(value as MaterialPickerTypeFilter)
            }
            options={[
              { label: "全部", value: "all" },
              { label: "表情", value: "sticker" },
              { label: "图片", value: "image" },
            ]}
          />
          <Input.Search
            allowClear
            placeholder="搜索名称 / 备注"
            style={{ width: 260 }}
            onSearch={(value) => {
              setMaterialKeyword(value);
              void loadMaterialPickerList(materialTypeFilter, value);
            }}
            onChange={(event) => {
              if (!event.target.value && materialKeyword) {
                setMaterialKeyword("");
                void loadMaterialPickerList(materialTypeFilter, "");
              }
            }}
          />
          <Spin spinning={materialLoading || materialBinding}>
            {!materialList.length ? (
              <Empty description="暂无可用素材，请先到「素材列表」上传" />
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                  gap: 12,
                }}
              >
                {materialList.map((item) => {
                  const url =
                    String(item.ossUrl || "").trim() ||
                    String(item.mediaProxyUrl || "").trim() ||
                    (item.id
                      ? `/api/script-material-media/${encodeURIComponent(String(item.id))}`
                      : "");
                  const typeLabel =
                    item.type === "sticker"
                      ? "表情"
                      : item.type === "image"
                        ? "图片"
                        : String(item.type || "");
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => void bindMaterial(String(item.id))}
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: 8,
                        background: "#fff",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      {url ? (
                        <img
                          src={url}
                          alt={item.name}
                          style={{
                            width: "100%",
                            height: 88,
                            objectFit: "contain",
                            background: "#f5f5f5",
                            borderRadius: 4,
                          }}
                        />
                      ) : (
                        <div style={{ height: 88, background: "#f5f5f5" }} />
                      )}
                      <Text ellipsis style={{ display: "block", marginTop: 6, fontSize: 12 }}>
                        {item.name || "未命名"}
                      </Text>
                      {item.remark ? (
                        <Text
                          type="secondary"
                          ellipsis
                          title={item.remark}
                          style={{ display: "block", marginTop: 2, fontSize: 11 }}
                        >
                          {item.remark}
                        </Text>
                      ) : null}
                      {typeLabel ? (
                        <Tag style={{ marginTop: 4 }}>{typeLabel}</Tag>
                      ) : null}
                      {item.dHashStatus === "failed" ? (
                        <Text type="danger" style={{ fontSize: 11 }}>
                          dHash 失败
                        </Text>
                      ) : item.dHash ? (
                        <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                          {item.dHash}
                        </Text>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </Spin>
        </Space>
      </Modal>
      <input
        ref={rowImageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          const row = pendingUploadRowRef.current;
          event.target.value = "";
          pendingUploadRowRef.current = null;
          if (file && row) void uploadRowImageFile(row, file);
        }}
      />
    </Space>
  );
}
